// src/lib/learningFeedback.js

const FEEDBACK_TABLE = "learning_feedback";
const MAX_COMMENT_LENGTH = 2000;

export const FREE_LESSONS_NOTICE_LEVEL = 4;

export const GOOGLE_REVIEW_URL = (
  process.env.REACT_APP_GOOGLE_REVIEW_URL ||
  "https://g.page/r/CTiWonh5Aox1EBM/review"
).trim();

export const FEEDBACK_TYPES = Object.freeze({
  GOOGLE_REVIEW: "google_review",
  LEVEL_DIFFICULTY: "level_difficulty",
  QUEST_FEEDBACK: "quest_feedback",
});

export const FEEDBACK_STATUSES = Object.freeze({
  SUBMITTED: "submitted",
  DISMISSED: "dismissed",
  OPENED_GOOGLE: "opened_google",
});

function requireString(value, fieldName) {
  const normalized = String(value ?? "").trim();

  if (!normalized) {
    throw new Error(`${fieldName} is required.`);
  }

  return normalized;
}

function asPlainObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return value;
}

function isDuplicateError(error) {
  return (
    String(error?.code || "") === "23505" ||
    Number(error?.status) === 409
  );
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const numberValue = Number(value);

    if (Number.isFinite(numberValue)) {
      return numberValue;
    }
  }

  return null;
}

/**
 * Extracts the global journey level number.
 *
 * Examples:
 * L1   -> 1
 * L25  -> 25
 * L105 -> 105
 * 5    -> 5
 */
export function getJourneyLevelNumber(levelKey) {
  const match = String(levelKey ?? "")
    .trim()
    .match(/^L?(\d+)$/i);

  if (!match?.[1]) {
    return null;
  }

  const levelNumber = Number.parseInt(match[1], 10);

  return Number.isInteger(levelNumber) && levelNumber > 0
    ? levelNumber
    : null;
}

/**
 * Normalises a level key to the format expected by the database.
 *
 * Examples:
 * l5 -> L5
 * 10 -> L10
 */
export function normalizeJourneyLevelKey(levelKey) {
  const levelNumber = getJourneyLevelNumber(levelKey);

  return levelNumber ? `L${levelNumber}` : null;
}

/**
 * Difficulty feedback appears:
 * - After Level 1
 * - After every fifth level thereafter
 *
 * Examples:
 * L1, L5, L10, L15, L20 ... L105
 */
export function isDifficultyFeedbackLevel(levelNumberOrKey) {
  const levelNumber = Number.isInteger(levelNumberOrKey)
    ? levelNumberOrKey
    : getJourneyLevelNumber(levelNumberOrKey);

  return (
    Number.isInteger(levelNumber) &&
    levelNumber > 0 &&
    (levelNumber === 1 || levelNumber % 5 === 0)
  );
}

/**
 * The free-lessons notice appears after Level 4 is completed
 * for the first time.
 */
export function isFreeLessonsNoticeLevel(levelNumberOrKey) {
  const levelNumber = Number.isInteger(levelNumberOrKey)
    ? levelNumberOrKey
    : getJourneyLevelNumber(levelNumberOrKey);

  return levelNumber === FREE_LESSONS_NOTICE_LEVEL;
}

/**
 * Use this from LevelResult to decide whether the Level 4
 * free-lessons notice should appear.
 */
export function shouldShowFreeLessonsNotice({
  isFirstCompletion,
  passed = true,
  levelKey,
  levelNumber,
} = {}) {
  const resolvedLevelNumber = Number.isInteger(levelNumber)
    ? levelNumber
    : getJourneyLevelNumber(levelKey);

  return (
    passed !== false &&
    !!isFirstCompletion &&
    isFreeLessonsNoticeLevel(resolvedLevelNumber)
  );
}

/**
 * Reads quest progress in a flexible way because different helpers
 * may return slightly different field names.
 */
export function getQuestProgressCounts(questProgress) {
  const total = firstFiniteNumber(
    questProgress?.total,
    questProgress?.totalLessons,
    questProgress?.total_lessons,
    questProgress?.lessonTotal,
    questProgress?.lesson_total
  );

  const completed = firstFiniteNumber(
    questProgress?.completed,
    questProgress?.completedLessons,
    questProgress?.completed_lessons,
    questProgress?.completedCount,
    questProgress?.completed_count
  );

  const percent = firstFiniteNumber(
    questProgress?.percent,
    questProgress?.completionPercent,
    questProgress?.completion_percent
  );

  return {
    total,
    completed,
    percent,
  };
}

/**
 * Uses exact lesson counts where available, and falls back to
 * completion percent when the progress helper returns percent only.
 *
 * This supports quests with either:
 * - 9 lessons
 * - 12 lessons
 */
export function isQuestProgressComplete(questProgress) {
  const { total, completed, percent } =
    getQuestProgressCounts(questProgress);

  const completeByCount =
    Number.isFinite(total) &&
    total > 0 &&
    Number.isFinite(completed) &&
    completed >= total;

  const completeByPercent =
    Number.isFinite(percent) && percent >= 100;

  return completeByCount || completeByPercent;
}

/**
 * Builds the feedback events for a newly completed level.
 *
 * Events are returned in this order:
 * 1. Quest completion feedback
 * 2. Difficulty rating
 * 3. Google review
 *
 * Resource-template popups and the Level 4 free-lessons notice
 * are deliberately not handled here.
 */
export function buildLearningFeedbackEvents({
  isFirstCompletion,
  questProgress,
  questId,
  subquestId,
  levelKey,
  levelTitle = "",
}) {
  if (!isFirstCompletion) {
    return [];
  }

  const levelNumber = getJourneyLevelNumber(levelKey);
  const normalizedLevelKey = normalizeJourneyLevelKey(levelKey);

  if (!levelNumber || !normalizedLevelKey) {
    throw new Error(`Invalid level key: ${String(levelKey ?? "")}`);
  }

  const normalizedQuestId = requireString(questId, "questId");
  const normalizedSubquestId = requireString(
    subquestId,
    "subquestId"
  );

  const normalizedLevelTitle = String(levelTitle ?? "").trim();

  const baseEvent = {
    questId: normalizedQuestId,
    subquestId: normalizedSubquestId,
    levelKey: normalizedLevelKey,
    levelNumber,

    metadata: {
      source: "level_result",

      ...(normalizedLevelTitle
        ? {
            level_title: normalizedLevelTitle,
          }
        : {}),
    },
  };

  const events = [];
  const questProgressCounts = getQuestProgressCounts(questProgress);

  /*
   * Show once after the first completion of every quest.
   */
  if (isQuestProgressComplete(questProgress)) {
    events.push({
      ...baseEvent,

      eventKey: `quest_feedback:quest_${normalizedQuestId}`,

      feedbackType: FEEDBACK_TYPES.QUEST_FEEDBACK,

      metadata: {
        ...baseEvent.metadata,

        ...(Number.isFinite(questProgressCounts.total)
          ? {
              quest_total_lessons: questProgressCounts.total,
            }
          : {}),

        ...(Number.isFinite(questProgressCounts.completed)
          ? {
              quest_completed_lessons: questProgressCounts.completed,
            }
          : {}),

        ...(Number.isFinite(questProgressCounts.percent)
          ? {
              quest_completion_percent: questProgressCounts.percent,
            }
          : {}),
      },
    });
  }

  /*
   * Show after L1 and every fifth level.
   */
  if (isDifficultyFeedbackLevel(levelNumber)) {
    events.push({
      ...baseEvent,

      eventKey: `level_difficulty:${levelNumber}`,

      feedbackType: FEEDBACK_TYPES.LEVEL_DIFFICULTY,
    });
  }

  /*
   * Show the Google review prompt after L3.
   */
  if (levelNumber === 3) {
    events.push({
      ...baseEvent,

      eventKey: "google_review:level_3",

      feedbackType: FEEDBACK_TYPES.GOOGLE_REVIEW,
    });
  }

  return events;
}

/**
 * Removes events that the user has already:
 * - Submitted
 * - Dismissed
 * - Opened on Google
 *
 * This prevents the same feedback prompt from appearing again
 * on another browser or device.
 */
export async function getUnrecordedLearningFeedbackEvents(
  supabase,
  userId,
  candidateEvents
) {
  if (!supabase) {
    throw new Error("Supabase client is required.");
  }

  const normalizedUserId = requireString(userId, "userId");

  /*
   * Remove duplicate candidate events before querying Supabase.
   */
  const uniqueEvents = Array.from(
    new Map(
      (candidateEvents || [])
        .filter(Boolean)
        .map((event) => [
          requireString(event.eventKey, "eventKey"),
          event,
        ])
    ).values()
  );

  if (uniqueEvents.length === 0) {
    return [];
  }

  const eventKeys = uniqueEvents.map((event) => event.eventKey);

  const { data, error } = await supabase
    .from(FEEDBACK_TABLE)
    .select("event_key")
    .eq("user_id", normalizedUserId)
    .in("event_key", eventKeys);

  if (error) {
    throw error;
  }

  const recordedEventKeys = new Set(
    (data || []).map((row) => row.event_key)
  );

  return uniqueEvents.filter(
    (event) => !recordedEventKeys.has(event.eventKey)
  );
}

/**
 * Inserts one immutable feedback result into Supabase.
 *
 * Difficulty submitted:
 * {
 *   status: "submitted",
 *   difficultyRating: 4
 * }
 *
 * Difficulty dismissed:
 * {
 *   status: "dismissed"
 * }
 *
 * Quest comment:
 * {
 *   status: "submitted",
 *   comment: "More examples would help."
 * }
 *
 * Quest "Everything is good":
 * {
 *   status: "submitted",
 *   comment: null,
 *   metadata: {
 *     response_action: "everything_good"
 *   }
 * }
 *
 * Google review link opened:
 * {
 *   status: "opened_google"
 * }
 *
 * Google review dismissed:
 * {
 *   status: "dismissed"
 * }
 */
export async function recordLearningFeedback(
  supabase,
  {
    userId,
    event,
    status,
    difficultyRating = null,
    comment = null,
    metadata = {},
  }
) {
  if (!supabase) {
    throw new Error("Supabase client is required.");
  }

  if (!event) {
    throw new Error("Feedback event is required.");
  }

  const normalizedUserId = requireString(userId, "userId");

  const eventKey = requireString(
    event.eventKey,
    "event.eventKey"
  );

  const feedbackType = requireString(
    event.feedbackType,
    "event.feedbackType"
  );

  const normalizedStatus = requireString(status, "status");

  const questId = requireString(
    event.questId,
    "event.questId"
  );

  const subquestId = requireString(
    event.subquestId,
    "event.subquestId"
  );

  const levelNumber = Number(event.levelNumber);

  if (
    !Number.isInteger(levelNumber) ||
    levelNumber <= 0
  ) {
    throw new Error(
      "event.levelNumber must be a positive integer."
    );
  }

  const levelKey = `L${levelNumber}`;

  let normalizedRating = null;
  let normalizedComment = null;

  /*
   * Validate difficulty feedback.
   */
  if (feedbackType === FEEDBACK_TYPES.LEVEL_DIFFICULTY) {
    if (normalizedStatus === FEEDBACK_STATUSES.SUBMITTED) {
      const rating = Number(difficultyRating);

      if (
        !Number.isInteger(rating) ||
        rating < 1 ||
        rating > 5
      ) {
        throw new Error(
          "Difficulty rating must be an integer from 1 to 5."
        );
      }

      normalizedRating = rating;
    } else if (
      normalizedStatus !== FEEDBACK_STATUSES.DISMISSED
    ) {
      throw new Error(
        "Invalid status for level difficulty feedback."
      );
    }
  }

  /*
   * Validate quest feedback.
   */
  else if (feedbackType === FEEDBACK_TYPES.QUEST_FEEDBACK) {
    if (
      normalizedStatus !== FEEDBACK_STATUSES.SUBMITTED &&
      normalizedStatus !== FEEDBACK_STATUSES.DISMISSED
    ) {
      throw new Error(
        "Invalid status for quest feedback."
      );
    }

    if (normalizedStatus === FEEDBACK_STATUSES.SUBMITTED) {
      const trimmedComment = String(comment ?? "").trim();

      if (trimmedComment.length > MAX_COMMENT_LENGTH) {
        throw new Error(
          `Quest feedback cannot exceed ${MAX_COMMENT_LENGTH} characters.`
        );
      }

      /*
       * Null represents the user's:
       * "Everything is good" response.
       */
      normalizedComment = trimmedComment || null;
    }
  }

  /*
   * Validate Google review activity.
   */
  else if (feedbackType === FEEDBACK_TYPES.GOOGLE_REVIEW) {
    if (
      normalizedStatus !== FEEDBACK_STATUSES.OPENED_GOOGLE &&
      normalizedStatus !== FEEDBACK_STATUSES.DISMISSED
    ) {
      throw new Error(
        "Invalid status for Google review feedback."
      );
    }
  } else {
    throw new Error(
      `Unsupported feedback type: ${feedbackType}`
    );
  }

  const payload = {
    user_id: normalizedUserId,

    event_key: eventKey,

    feedback_type: feedbackType,

    status: normalizedStatus,

    quest_id: questId,

    subquest_id: subquestId,

    level_key: levelKey,

    level_number: levelNumber,

    difficulty_rating: normalizedRating,

    comment: normalizedComment,

    metadata: {
      ...asPlainObject(event.metadata),
      ...asPlainObject(metadata),
    },
  };

  const { data, error } = await supabase
    .from(FEEDBACK_TABLE)
    .insert(payload)
    .select(
      "id, event_key, feedback_type, status, created_at"
    )
    .single();

  if (!error) {
    return {
      row: data,
      duplicate: false,
    };
  }

  /*
   * A duplicate means the feedback event has already
   * been recorded for this user.
   */
  if (!isDuplicateError(error)) {
    throw error;
  }

  const { data: existing, error: existingError } =
    await supabase
      .from(FEEDBACK_TABLE)
      .select(
        "id, event_key, feedback_type, status, created_at"
      )
      .eq("user_id", normalizedUserId)
      .eq("event_key", eventKey)
      .maybeSingle();

  if (existingError) {
    throw existingError;
  }

  return {
    row: existing || null,
    duplicate: true,
  };
}